/**
 * Analytics service - sends events to server PostHog via /api/analytics/track-batch.
 * Events are queued and batched to reduce HTTP requests when many events fire (e.g. player enters room).
 * Fire-and-forget; does not block the main flow.
 * All events include url, pathname, and screen for context.
 */

import { getServerBaseUrl } from '@/utils/serverUrl'
import Parse from 'parse'

/** Screen categories for analytics context (Authentication, Livekit, RoomEvent, etc.) */
export type AnalyticsScreen =
  | 'Authentication'
  | 'Lobby'
  | 'Room'
  | 'Livekit'
  | 'MyRooms'
  | 'Profile'
  | 'Settings'
  | 'Admin'
  | 'Company'
  | 'Other'

/**
 * Derive screen category from pathname for analytics.
 */
function getScreenFromPathname(pathname: string): AnalyticsScreen {
  if (!pathname || pathname === '/') return 'Lobby'
  const p = pathname.toLowerCase()
  if (p.startsWith('/auth') || p.includes('/callback') || p === '/verify-email' || p.startsWith('/reset-password'))
    return 'Authentication'
  if (p.startsWith('/admin')) return 'Admin'
  if (p.startsWith('/company')) return 'Company'
  if (p.startsWith('/profile')) return 'Profile'
  if (p === '/settings') return 'Settings'
  if (p === '/rooms' || p === '/myrooms') return 'MyRooms'
  if (p === '/dashboard' || p === '/publiclobby' || p === '/join' || p === '/room') return 'Lobby'
  return 'Other'
}

export interface AnalyticsContext {
  url: string
  pathname: string
  screen: AnalyticsScreen
}

/**
 * Get current URL/screen context for analytics. Safe to call in SSR (returns empty strings).
 */
export function getAnalyticsContext(screenOverride?: AnalyticsScreen): AnalyticsContext {
  if (typeof window === 'undefined') {
    return { url: '', pathname: '', screen: screenOverride || 'Other' }
  }
  const pathname = window.location.pathname || '/'
  const url = window.location.href || ''
  const screen = screenOverride ?? getScreenFromPathname(pathname)
  return { url, pathname, screen }
}

/** Omit undefined keys so POST bodies and PostHog props stay clean (null is retained). */
export function stripUndefinedAnalyticsProps(obj: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = { ...obj }
  for (const k of Object.keys(out)) {
    if (out[k] === undefined) delete out[k]
  }
  return out
}

/**
 * SC-209: which engine this was, coarsely.
 *
 * Media behaviour splits by engine more than by anything else we record — picture-in-picture, autoplay,
 * screen share and wake locks all differ — and the batch route's server-side reading of the user agent cannot
 * see a browser embedded in another application, which is where the reports we cannot reproduce come from.
 *
 * This is the one place user-agent inspection is acceptable: it labels a datapoint and decides nothing. No
 * feature anywhere may branch on it. Chromium's `userAgentData` is preferred because it is the supported way
 * to ask; the string is the fallback for everyone else. Deliberately four or five buckets — a version number
 * would be a fingerprint, and we do not need one to tell Safari from Chrome.
 */
function coarseBrowser(): string | undefined {
  if (typeof navigator === 'undefined') return undefined

  const brands = (navigator as Navigator & { userAgentData?: { brands?: Array<{ brand: string }> } })
    .userAgentData?.brands
  if (brands?.length) {
    const named = brands
      .map((entry) => entry.brand)
      // Chromium seeds this list with a deliberately meaningless brand to stop naive parsing.
      .filter((brand) => !/not.a.brand/i.test(brand))
    if (named.some((brand) => /edge/i.test(brand))) return 'Edge'
    if (named.some((brand) => /opera|opr/i.test(brand))) return 'Opera'
    if (named.some((brand) => /google chrome/i.test(brand))) return 'Chrome'
    if (named.some((brand) => /chromium/i.test(brand))) return 'Chromium'
  }

  const ua = navigator.userAgent || ''
  if (!ua) return undefined
  if (/Edg\//.test(ua)) return 'Edge'
  if (/OPR\/|Opera/.test(ua)) return 'Opera'
  if (/Firefox\//.test(ua)) return 'Firefox'
  if (/Chrome\//.test(ua)) return 'Chrome'
  if (/Safari\//.test(ua)) return 'Safari'
  return 'Other'
}

function coarseOs(): string | undefined {
  if (typeof navigator === 'undefined') return undefined

  const platform = (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform
  if (platform) return platform

  const ua = navigator.userAgent || ''
  if (!ua) return undefined
  if (/Android/.test(ua)) return 'Android'
  if (/iPhone|iPad|iPod/.test(ua)) return 'iOS'
  if (/Mac OS X|Macintosh/.test(ua)) return 'macOS'
  if (/Windows/.test(ua)) return 'Windows'
  if (/Linux/.test(ua)) return 'Linux'
  return 'Other'
}

/**
 * LiveKit-related client events: explicit Colyseus vs LiveKit naming; `roomId` mirrors Colyseus id for legacy
 * dashboards.
 *
 * **The engine properties are deliberately NOT `$browser`/`$os`.** Those are PostHog's own reserved names,
 * filled from its own vocabulary ("Mac OS X", "Microsoft Edge") by every event posthog-js captures directly.
 * Writing ours into them would not sit beside PostHog's — it would overwrite them, with a different
 * vocabulary, on this subset of events only, so a breakdown spanning both would split one machine across two
 * buckets and the change would silently alter what historical columns mean. Our own names keep the two
 * readable side by side.
 *
 * Coarse on purpose — four or five buckets, no version — because media behaviour splits by engine and a
 * version string would be a fingerprint. Worth remembering that this is on *every* LiveKit event, not just
 * the picture-in-picture ones, so it belongs on the SC-351/SC-359 data register.
 */
export function livekitAnalyticsBaseProps(
  colyseusRoomId?: string | null,
  livekitRoomName?: string | null
): Record<string, unknown> {
  return stripUndefinedAnalyticsProps({
    colyseus_room_id: colyseusRoomId ? String(colyseusRoomId) : undefined,
    livekit_room_name: livekitRoomName ? String(livekitRoomName) : undefined,
    roomId: colyseusRoomId ? String(colyseusRoomId) : undefined,
    client_browser: coarseBrowser(),
    client_os: coarseOs(),
  })
}

/**
 * Consume `sessionStorage.app_join_from_invite` once and emit APP_invite_accepted (centralized vs RoomHandler duplicate).
 */
export function trackInviteAcceptedWhenQueued(colyseusRoomId?: string | null): void {
  if (typeof sessionStorage === 'undefined') return
  if (!sessionStorage.getItem('app_join_from_invite')) return
  sessionStorage.removeItem('app_join_from_invite')
  trackEvent(
    'APP_invite_accepted',
    stripUndefinedAnalyticsProps({
      colyseus_room_id: colyseusRoomId ? String(colyseusRoomId) : undefined,
      join_source: 'invite_link_session',
      roomId: colyseusRoomId ? String(colyseusRoomId) : undefined,
    }),
    'Room'
  )
}

const ALLOWLISTED_EVENTS = [
  'APP_livekit_connected',
  'APP_livekit_disconnected',
  'APP_livekit_reconnected',
  'APP_livekit_connection_failed',
  'APP_livekit_audio_toggled',
  'APP_livekit_video_toggled',
  'APP_livekit_screen_share_started',
  'APP_livekit_screen_share_ended',
  'APP_livekit_audio_playback_blocked',
  'APP_livekit_audio_playback_resumed',
  'APP_avatar_preset_selected',
  'APP_avatar_saved',
  'APP_space_entered',
  'APP_workspace_entered',
  'APP_onboarding_started',
  'APP_onboarding_completed',
  'APP_onboarding_step_completed',
  'APP_invite_sent',
  'APP_invite_accepted',
  'APP_permissions_allowed',
  'APP_mainmenu_viewed',
  'APP_mainmenu_refreshed',
  'APP_product_updates_opened',
  'APP_product_updates_tab',
  'APP_product_updates_saved',
  'APP_product_updates_vote_cast',
  'APP_product_updates_vote_revoke',
  'APP_product_updates_vote_report_loaded',
  'APP_product_updates_admin_promote_top_vote',
  'APP_product_updates_admin_duplicate_from_vote_report',
  'APP_open_space_room_chat',
  'APP_analytics_session_started',
  'APP_company_creation_success',
  'APP_colyseus_reconnected',
  'APP_colyseus_reconnect_failed',
  // SC-209 picture-in-picture.
  'APP_pip_opened',
  'APP_pip_closed',
  'APP_pip_open_failed',
  'APP_pip_content_switched',
  'APP_pip_unsupported',
] as const

const ALLOWLISTED_SET = new Set<string>(ALLOWLISTED_EVENTS)

export type AnalyticsEvent = (typeof ALLOWLISTED_EVENTS)[number]

/** Passed to `/api/analytics/login` after explicit auth success (signup, login, OAuth, invite). */
export type AnalyticsAuthFlow = 'registration' | 'login' | 'oauth_google' | 'invitation_magic_link'

export type AnalyticsAuthMethod = 'password' | 'google' | 'invitation_magic_link'

export interface TrackAuthSuccessOptions {
  authFlow: AnalyticsAuthFlow
  authMethod?: AnalyticsAuthMethod
  companyAssigned?: boolean
  companyId?: string | null
  companyName?: string | null
  email?: string
  username?: string
}

/** Queued event with merged properties (url, pathname, screen included) */
interface QueuedEvent {
  event: string
  properties: Record<string, unknown>
}

const BATCH_SIZE_THRESHOLD = 10
const MAX_BATCH_SIZE = 25
const FLUSH_DEBOUNCE_MS = 500

let queue: QueuedEvent[] = []
let flushTimeoutId: ReturnType<typeof setTimeout> | null = null
let flushInProgress = false

function scheduleFlush(): void {
  if (flushInProgress) return
  if (queue.length === 0) return

  if (queue.length >= BATCH_SIZE_THRESHOLD) {
    flushTimeoutId && clearTimeout(flushTimeoutId)
    flushTimeoutId = null
    flush()
    return
  }

  if (!flushTimeoutId) {
    flushTimeoutId = setTimeout(() => {
      flushTimeoutId = null
      flush()
    }, FLUSH_DEBOUNCE_MS)
  }
}

function flush(): void {
  if (flushInProgress || queue.length === 0) return

  const currentUser = Parse.User.current()
  const sessionToken = currentUser?.getSessionToken?.()
  if (!sessionToken) {
    queue = []
    return
  }

  flushInProgress = true
  const batch = queue.splice(0, MAX_BATCH_SIZE)
  const events = batch.map((e) => ({ event: e.event, properties: e.properties }))

  const baseUrl = getServerBaseUrl()
  const url = `${baseUrl}/api/analytics/track-batch`

  fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${sessionToken}`,
    },
    body: JSON.stringify({ events }),
    keepalive: true,
  })
    .catch(() => {
      // Silently ignore - analytics must not affect UX
    })
    .finally(() => {
      flushInProgress = false
      if (queue.length > 0) {
        flush()
      }
    })
}

if (typeof window !== 'undefined') {
  const handleUnload = () => {
    flushTimeoutId && clearTimeout(flushTimeoutId)
    flushTimeoutId = null
    flush()
  }
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      handleUnload()
    }
  })
  window.addEventListener('pagehide', handleUnload)
}

function authMethodDefault(flow: AnalyticsAuthFlow): AnalyticsAuthMethod {
  if (flow === 'registration' || flow === 'login') return 'password'
  if (flow === 'oauth_google') return 'google'
  return 'invitation_magic_link'
}

/**
 * Track an event. Fire-and-forget (no await). Requires authenticated user.
 * Events are queued and batched; flushed when queue reaches 10, after 500ms debounce, or on page unload.
 * Automatically includes url, pathname, screen. Pass screenOverride for known context (e.g. "Room" for LiveKit events).
 */
export function trackEvent(
  event: AnalyticsEvent | string,
  properties?: Record<string, unknown>,
  screenOverride?: AnalyticsScreen
): void {
  if (!ALLOWLISTED_SET.has(event)) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(`[Analytics] Event not allowlisted: ${event}`)
    }
    return
  }

  const currentUser = Parse.User.current()
  const sessionToken = currentUser?.getSessionToken?.()
  if (!sessionToken) return

  const email = currentUser?.get?.('email') as string | undefined
  const username = currentUser?.get?.('username') as string | undefined
  const personSet: Record<string, unknown> = {}
  if (email) personSet.email = email
  if (username) personSet.username = username

  const context = getAnalyticsContext(screenOverride)
  const mergedProperties = stripUndefinedAnalyticsProps({
    ...(properties || {}),
    url: context.url,
    pathname: context.pathname,
    screen: context.screen,
    ...(Object.keys(personSet).length > 0 ? { $set_once: personSet } : {}),
  })

  queue.push({ event, properties: mergedProperties })
  scheduleFlush()
}

/**
 * After successful explicit authentication (signup, sign-in, OAuth, invitation).
 * Sends one PostHog identify + APP_registration_completed or APP_login_success immediately (not queued).
 */
export function trackAuthSuccess(opts: TrackAuthSuccessOptions): void {
  const currentUser = Parse.User.current()
  const sessionToken = currentUser?.getSessionToken?.()
  if (!sessionToken) return

  const authMethod = opts.authMethod ?? authMethodDefault(opts.authFlow)
  const context = getAnalyticsContext()
  const baseUrl = getServerBaseUrl()
  const url = `${baseUrl}/api/analytics/login`

  fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${sessionToken}`,
    },
    body: JSON.stringify(
      stripUndefinedAnalyticsProps({
      url: context.url,
      pathname: context.pathname,
      screen: context.screen,
      auth_flow: opts.authFlow,
      auth_method: authMethod,
      company_assigned:
        opts.companyAssigned === undefined ? undefined : !!opts.companyAssigned,
      company_id: opts.companyId ?? undefined,
      company_name: opts.companyName ?? undefined,
      email: opts.email || undefined,
      username: opts.username || undefined,
      })
    ),
    keepalive: true,
  }).catch(() => {
    // Silently ignore
  })
}
